import { useMemo, useState } from 'react'
import { FormField } from '@/components/ui/form-field'
import { Input } from '@/components/ui/input'
import { NativeSelect } from '@/components/ui/native-select'
import { shortDay } from '../../record/format'
import { STATUS_LABEL } from '../reviewModel'
import type { AgreementChange, ChangeValues, FollowUp, StatusChange } from '../types'
import { FollowUpFields } from './FollowUpFields'
import { SheetFrame } from './SheetFrame'
import { followUpInput, useSchedulePreview } from './useSchedulePreview'

/** Remove what is empty, so the stored change only holds what it really changes. */
function cleaned(change: ChangeValues): ChangeValues {
  const entries = Object.entries(change).filter(([, value]) => value !== null && value !== undefined && value !== '')
  return Object.fromEntries(entries) as ChangeValues
}

/**
 * Edit a proposed change of a running agreement, with a live preview of the deadlines.
 * The agreement is the one Sparky linked; when it offered several (or none was clear) the
 * picklist lets the user choose which one is meant.
 */
export function AgreementChangeSheet({
  change,
  onSave,
  onClose,
}: {
  change: AgreementChange
  onSave: (values: Record<string, unknown>) => void
  onClose: () => void
}) {
  const [agreementId, setAgreementId] = useState(change.agreement_id ?? '')
  const options = useMemo(() => {
    const list = change.candidates.map((choice) => ({
      id: choice.id,
      title: choice.title,
      label: choice.rhythm ? `${choice.title} · ${choice.rhythm}` : choice.title,
    }))
    if (change.agreement_id && !list.some((option) => option.id === change.agreement_id)) {
      const title = change.agreement_title ?? change.agreement_text ?? 'Gekoppelde afspraak'
      list.unshift({ id: change.agreement_id, title, label: title })
    }
    return list
  }, [change.candidates, change.agreement_id, change.agreement_title, change.agreement_text])
  const [follow, setFollow] = useState<FollowUp>(change.change.follow_up ?? {})
  const [status, setStatus] = useState<StatusChange | ''>(change.change.status ?? '')
  const [ends, setEnds] = useState(change.change.ends_on ?? '')
  const [postpone, setPostpone] = useState(change.change.postpone_until ?? '')

  const request = useMemo(() => {
    const input = followUpInput(follow)
    return input && agreementId ? { ...input, agreement_id: agreementId } : null
  }, [follow, agreementId])
  const { preview, problem } = useSchedulePreview(request)

  return (
    <SheetFrame
      title="Afspraakwijziging bewerken"
      onClose={onClose}
      saveDisabled={!agreementId}
      onSave={() =>
        onSave({
          ...(agreementId !== (change.agreement_id ?? '')
            ? {
                agreement_id: agreementId,
                agreement_title: options.find((option) => option.id === agreementId)?.title ?? null,
              }
            : {}),
          change: cleaned({
            ...change.change,
            follow_up: follow.schedule ? follow : null,
            status: status || null,
            ends_on: ends || null,
            postpone_until: postpone || null,
          }),
        })
      }
    >
      <FormField label="Afspraak" required>
        {options.length > 0 ? (
          <NativeSelect value={agreementId} onChange={(event) => setAgreementId(event.target.value)}>
            {agreementId ? null : <option value="">Kies welke afspraak je bedoelt</option>}
            {options.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </NativeSelect>
        ) : (
          <p className="text-base text-muted-foreground">
            Deze relatie heeft geen lopende afspraak om te wijzigen. Maak er een aan op de pagina van de relatie.
          </p>
        )}
      </FormField>
      <FollowUpFields value={follow} onChange={setFollow} unchanged />
      {preview && preview.deadlines.length > 0 ? (
        <p className="text-sm" aria-live="polite">
          {`Volgende deadlines: ${preview.deadlines.map((day) => shortDay(day)).join(' · ')}`}
        </p>
      ) : null}
      {preview?.warnings.map((warning) => (
        <p key={warning} className="text-sm text-muted-foreground">
          {warning}
        </p>
      ))}
      {problem ? (
        <p role="alert" className="text-sm text-destructive">
          {problem}
        </p>
      ) : null}
      <FormField label="Status">
        <NativeSelect value={status} onChange={(event) => setStatus(event.target.value as StatusChange | '')}>
          <option value="">Niet wijzigen</option>
          {(Object.keys(STATUS_LABEL) as StatusChange[]).map((key) => (
            <option key={key} value={key}>
              {STATUS_LABEL[key]}
            </option>
          ))}
        </NativeSelect>
      </FormField>
      <FormField label="Einddatum">
        <Input type="date" value={ends} onChange={(event) => setEnds(event.target.value)} />
      </FormField>
      <FormField label="Open taak uitstellen tot">
        <Input type="date" value={postpone} onChange={(event) => setPostpone(event.target.value)} />
      </FormField>
    </SheetFrame>
  )
}
